import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Stack,
  Paper,
  Group,
  Text,
  Button,
  Badge,
  Modal,
  TextInput,
  Select,
  NumberInput,
  Skeleton,
  Stepper,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconPlus,
  IconClipboardList,
} from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import EmptyState from '@/components/EmptyState';
import { useAuthStore } from '@/store/auth';
import { useMenus, useCreateMenu } from '@/hooks/api';

const CUISINE_TYPES = [
  { value: 'american', label: 'American' },
  { value: 'italian', label: 'Italian' },
  { value: 'mexican', label: 'Mexican' },
  { value: 'asian', label: 'Asian' },
  { value: 'french', label: 'French' },
  { value: 'mediterranean', label: 'Mediterranean' },
  { value: 'bbq', label: 'BBQ / Grill' },
  { value: 'seafood', label: 'Seafood' },
  { value: 'fusion', label: 'Fusion' },
  { value: 'other', label: 'Other' },
];

function getMenuStep(menu: { dishes: unknown[]; order_schedule: unknown[]; status: string }) {
  if (menu.order_schedule && menu.order_schedule.length > 0) return 3;
  if (menu.dishes && menu.dishes.length > 0) return 1;
  return 0;
}

export default function MyMenus() {
  const navigate = useNavigate();
  const customerId = useAuthStore((s) => s.user?.customer?.id);
  const { data: menusData, isLoading } = useMenus(customerId);
  const createMenu = useCreateMenu();
  const [opened, { open, close }] = useDisclosure(false);

  const [name, setName] = useState('');
  const [cuisineType, setCuisineType] = useState<string | null>(null);
  const [coverCount, setCoverCount] = useState<number>(50);
  const [servicesPerWeek, setServicesPerWeek] = useState<number>(5);

  const handleCreate = async () => {
    if (!name.trim()) {
      notifications.show({
        title: 'Missing Name',
        message: 'Please enter a menu name',
        color: 'orange',
      });
      return;
    }
    try {
      await createMenu.mutateAsync({
        name: name.trim(),
        cuisine_type: cuisineType || undefined,
        cover_count: coverCount,
        services_per_week: servicesPerWeek,
        customer_id: customerId,
      });
      notifications.show({
        title: 'Menu Created',
        message: `"${name}" has been created`,
        color: 'green',
      });
      close();
      setName('');
      setCuisineType(null);
      setCoverCount(50);
      setServicesPerWeek(5);
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to create menu',
        color: 'red',
      });
    }
  };

  const menus = menusData?.items || [];

  return (
    <Stack gap="lg">
      <PageHeader
        title="My Menus"
        description="Create and manage your menus"
        actions={
          <Button leftSection={<IconPlus size={16} />} onClick={open}>
            Create Menu
          </Button>
        }
      />

      {isLoading ? (
        <Stack gap="sm">
          <Skeleton height={100} radius="md" />
          <Skeleton height={100} radius="md" />
          <Skeleton height={100} radius="md" />
        </Stack>
      ) : menus.length === 0 ? (
        <Paper p="lg" radius="md" withBorder>
          <EmptyState
            title="No Menus Yet"
            description="Create your first menu to get started with AI-powered ingredient mapping and ordering."
            icon={<IconClipboardList size={48} />}
            action={
              <Button leftSection={<IconPlus size={16} />} onClick={open}>
                Create Menu
              </Button>
            }
          />
        </Paper>
      ) : (
        <Stack gap="sm">
          {menus.map((menu) => {
            const step = getMenuStep(menu);
            return (
              <Paper
                key={menu.id}
                p="lg"
                radius="md"
                withBorder
                onClick={() => navigate(`/menu-planner/menus/${menu.id}`)}
                style={{ cursor: 'pointer' }}
              >
                <Group justify="space-between" align="flex-start">
                  <Stack gap="xs" style={{ flex: 1 }}>
                    <Group gap="sm">
                      <Text fw={600}>{menu.name}</Text>
                      {menu.cuisine_type && (
                        <Badge variant="light" size="sm">{menu.cuisine_type}</Badge>
                      )}
                      <Badge
                        color={menu.status === 'active' ? 'green' : 'gray'}
                        variant="light"
                        size="sm"
                      >
                        {menu.status}
                      </Badge>
                    </Group>
                    <Text size="sm" c="dimmed">
                      {menu.cover_count} covers &middot; {menu.services_per_week} services/week
                      &middot; {menu.dishes?.length || 0} dishes
                    </Text>
                    <Stepper
                      active={step}
                      size="xs"
                      mt="xs"
                      styles={{
                        separator: { minWidth: 20 },
                      }}
                    >
                      <Stepper.Step label="Dishes" />
                      <Stepper.Step label="Map" />
                      <Stepper.Step label="Calculate" />
                      <Stepper.Step label="Order" />
                    </Stepper>
                  </Stack>
                </Group>
              </Paper>
            );
          })}
        </Stack>
      )}

      {/* Create Modal */}
      <Modal opened={opened} onClose={close} title="Create Menu" centered>
        <Stack gap="md">
          <TextInput
            label="Menu Name"
            placeholder="e.g. Spring Dinner Menu"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            required
          />
          <Select
            label="Cuisine Type"
            placeholder="Select cuisine"
            data={CUISINE_TYPES}
            value={cuisineType}
            onChange={setCuisineType}
            clearable
          />
          <NumberInput
            label="Cover Count"
            description="Number of guests per service"
            value={coverCount}
            onChange={(v) => setCoverCount(Number(v) || 1)}
            min={1}
            max={10000}
          />
          <NumberInput
            label="Services per Week"
            value={servicesPerWeek}
            onChange={(v) => setServicesPerWeek(Number(v) || 1)}
            min={1}
            max={21}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={close}>Cancel</Button>
            <Button onClick={handleCreate} loading={createMenu.isPending}>
              Create
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
